"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractColumnRefs = exports.isAggregateCall = void 0;
const functions_1 = require("./functions");
const utils_1 = require("./utils");
const AGGREGATE_FUNCTION_NAMES = new Set(functions_1.FUNCTION_DEFINITIONS.filter((f) => f.category === 'aggregate').map((f) => f.name));
// Recognises AST nodes that represent an aggregate function call.
// Used by the codegen dispatcher to decide whether to window-wrap output when
// `aggregateContext === 'window'`. Centralising this in one place means new
// aggregates added to `functions.ts` with category `'aggregate'` are picked up
// automatically — the codegen can't silently forget to wrap them.
// MIN/MAX are polymorphic (1-arg aggregate, 2-arg scalar LEAST/GREATEST) and
// live under category `'math'`, so they're handled explicitly.
const isAggregateCall = (node) => {
    switch (node.type) {
        case 'ConditionalAggregate':
        case 'CountIf':
            return true;
        case 'SingleArgFn':
        case 'ZeroOrOneArgFn':
            return AGGREGATE_FUNCTION_NAMES.has(node.name);
        case 'OneOrTwoArgFn':
            return ((node.name === 'MIN' || node.name === 'MAX') &&
                node.args.length === 1);
        case 'BinaryOp':
        case 'UnaryOp':
        case 'If':
        case 'ZeroArgFn':
        case 'VariadicFn':
        case 'WindowFn':
        case 'ColumnRef':
        case 'NumberLiteral':
        case 'StringLiteral':
        case 'BooleanLiteral':
        case 'Comparison':
        case 'Logical':
        case 'WindowClause':
            return false;
        default:
            return (0, utils_1.assertUnreachable)(node, `Unknown AST node type`);
    }
};
exports.isAggregateCall = isAggregateCall;
const extractColumnRefs = (node) => {
    switch (node.type) {
        case 'ColumnRef':
            return [node.name];
        case 'BinaryOp':
        case 'Comparison':
        case 'Logical':
            return [
                ...(0, exports.extractColumnRefs)(node.left),
                ...(0, exports.extractColumnRefs)(node.right),
            ];
        case 'UnaryOp':
            return (0, exports.extractColumnRefs)(node.operand);
        case 'If':
            return [
                ...(0, exports.extractColumnRefs)(node.condition),
                ...(0, exports.extractColumnRefs)(node.then),
                ...(node.else ? (0, exports.extractColumnRefs)(node.else) : []),
            ];
        case 'ConditionalAggregate':
            return [
                ...(0, exports.extractColumnRefs)(node.value),
                ...(0, exports.extractColumnRefs)(node.condition),
            ];
        case 'CountIf':
            return (0, exports.extractColumnRefs)(node.condition);
        case 'SingleArgFn':
            return (0, exports.extractColumnRefs)(node.arg);
        case 'ZeroOrOneArgFn':
            return node.arg ? (0, exports.extractColumnRefs)(node.arg) : [];
        case 'OneOrTwoArgFn':
        case 'VariadicFn':
            return node.args.flatMap(exports.extractColumnRefs);
        case 'WindowFn': {
            const argRefs = node.args.flatMap(exports.extractColumnRefs);
            const clauseRefs = node.windowClause
                ? (0, exports.extractColumnRefs)(node.windowClause)
                : [];
            return [...argRefs, ...clauseRefs];
        }
        case 'WindowClause': {
            const orderRefs = node.orderBy
                ? (0, exports.extractColumnRefs)(node.orderBy.column)
                : [];
            const partitionRefs = node.partitionBy
                ? (0, exports.extractColumnRefs)(node.partitionBy)
                : [];
            return [...orderRefs, ...partitionRefs];
        }
        case 'ZeroArgFn':
        case 'NumberLiteral':
        case 'StringLiteral':
        case 'BooleanLiteral':
            return [];
        default:
            return (0, utils_1.assertUnreachable)(node, `Unknown AST node type`);
    }
};
exports.extractColumnRefs = extractColumnRefs;
//# sourceMappingURL=ast.js.map